import {
  OPS_AUTH,
  USE_MOCK_AUTH,
  caseAssignPath,
  caseCallLogsPath,
  caseEscalatePath,
  casePath,
  casePaymentInitiatePath,
  paymentUpdatePath,
  caseSlaPath,
  institutionCustomersPath,
  institutionCustomersSummaryPath,
  institutionFileRecordsPath,
  institutionGroupCasesPath,
  institutionGroupUploadsPath,
  institutionPortfolioGroupPath,
  institutionPortfolioGroupsPath,
} from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";
import { resolveMediaUrl } from "../utils/resolveMediaUrl";

function toQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

const LIST_PAGE_LIMIT = 100;
const CASES_PAGE_LIMIT = 500;
const LIST_TTL_MS = 60_000;

async function fetchAllPages(token, path, extract, extraQuery = {}) {
  const rows = [];
  let page = 1;
  let lastPage = 1;

  do {
    const json = await apiRequest(
      `${path}${toQuery({ page, limit: LIST_PAGE_LIMIT, ...extraQuery })}`,
      {
        method: "GET",
        token,
      }
    );
    const data = unwrapApiEnvelope(json);
    rows.push(...(extract(data) || []));
    lastPage = Number(data?.pagination?.last_page || 1);
    page += 1;
  } while (page <= lastPage && page <= 40);

  return rows;
}

export function normalizeOpsCustomer(raw, institutionName = "") {
  if (!raw || typeof raw !== "object") return null;
  const owed = Number(raw.total_amount_owed ?? raw.amount_owed ?? raw.amountOwed ?? 0);
  const paid = Number(raw.total_amount_paid ?? raw.amount_paid ?? raw.amountPaid ?? 0);
  const phone = raw.phone_number || raw.phone || "";
  const institutionLabel = raw.provider_name || institutionName || "";
  return {
    id: String(raw.debt_customer_slug || raw.id || raw.case_id || raw.account_number || ""),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    debt_customer_slug: raw.debt_customer_slug || null,
    first_name: raw.first_name || "",
    last_name: raw.last_name || "",
    name: raw.fullname || raw.customer || raw.customer_name || raw.name,
    caseId: raw.case_id || raw.case?.case_id || null,
    accountNumber: raw.account_number || raw.accountNumber || "",
    customer_number: raw.customer_number || "",
    phone,
    phone_number: phone,
    email: raw.email || "",
    ghana_card_number: raw.ghanacard_id || raw.ghana_card_number || "",
    gender: raw.gender || "",
    region: raw.region || "",
    country: raw.country || "",
    exact_area: raw.location || raw.exact_area || "",
    loan_amount: Number(raw.loan_amount ?? 0),
    cases_count: Number(raw.cases_count ?? 0),
    dueDate: raw.loan_due_date || raw.due_date || raw.dueDate || null,
    institution: institutionLabel,
    institutionRecord: {
      name: institutionLabel,
      logo: resolveMediaUrl(raw.provider_logo || raw.logo || null),
      code: raw.provider_code || "",
      type: raw.provider_type || raw.business_type || "",
      region: raw.provider_region || "",
      status: raw.provider_status || "",
      provider_id: raw.provider_id ?? raw.provider_slug ?? null,
    },
    provider_id: raw.provider_id ?? raw.provider_slug ?? null,
    amountOwed: owed,
    amountPaid: paid,
    outstandingAmount: Math.max(0, owed - paid),
    daysOverdue: Number(raw.total_days_overdue ?? raw.days_overdue ?? 0),
    assignedAgent: raw.call_center_agent || null,
    caseStatus: raw.status || "active",
    status: raw.status || "active",
    updatedAt: raw.updated_at || raw.created_at || null,
  };
}

export function normalizeOpsGroup(raw, institutionName = "") {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: String(raw.id ?? (typeof raw.group_slug === "object" ? "" : raw.group_slug || "")),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    group_slug: typeof raw.group_slug === "object" ? String(raw.id ?? "") : raw.group_slug,
    name: raw.group_name || raw.name,
    description: raw.description || "",
    institution: raw.provider_name || institutionName,
    provider_id: raw.provider_id ?? raw.provider_slug ?? null,
    uploadsCount: Number(raw.uploads_count ?? 0),
    accounts: Number(raw.number_of_cases ?? raw.cases_count ?? 0),
    totalOwed: Number(raw.total_owed ?? 0),
    totalPaid: Number(raw.total_paid ?? 0),
    outstanding: Number(raw.outstanding ?? 0),
    lastUploadedAt: raw.last_uploaded_at || null,
    createdAt: raw.created_at || null,
    color: raw.color_tag || "red",
  };
}

export function normalizeOpsUpload(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: String(raw.id),
    numericId: raw.id,
    portfolio_group_slug: raw.portfolio_group_slug,
    fileName: raw.title || raw.file_name || "Upload",
    accounts: Number(raw.number_of_cases ?? raw.accounts ?? raw.validation_pass ?? 0),
    totalOwed: Number(raw.total_owed ?? raw.total_amount ?? 0),
    totalPaid: Number(raw.total_paid ?? 0),
    outstanding: Number(raw.outstanding ?? 0),
    invalidRows: Number(raw.invalid_rows ?? 0),
    uploadedAt: raw.created_at || raw.uploaded_at || null,
    status: raw.status === "failed" ? "failed" : raw.failed_at ? "failed" : "completed",
  };
}

export function normalizeOpsCase(raw, extras = {}) {
  if (!raw || typeof raw !== "object") return null;
  const owed = Number(raw.amount_owed ?? raw.outstanding_balance ?? raw.amountOwed ?? 0);
  const paid = Number(raw.amount_paid ?? raw.amountPaid ?? 0);
  let outstanding = raw.outstanding != null && raw.outstanding !== ""
    ? Number(raw.outstanding)
    : Math.max(0, owed - paid);
  if (paid > 0 && owed > paid && outstanding >= owed) {
    outstanding = Math.max(0, owed - paid);
  }
  const assignedAgent = raw.call_center_agent || raw.assignedAgent || null;
  return {
    id: raw.case_code || raw.case_id || String(raw.id || raw.account_number || ""),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    case_id: raw.case_id || null,
    customer_number: raw.customer_number || raw.customerNumber || "",
    first_name: raw.first_name || "",
    last_name: raw.last_name || "",
    customer: raw.fullname || raw.customer_name || raw.customer,
    customer_name: raw.fullname || raw.customer_name || raw.customer,
    accountNumber: raw.account_number || raw.accountNumber || "",
    account_number: raw.account_number || raw.accountNumber || "",
    phone: raw.phone_number || raw.phone || "",
    email: raw.email || "",
    ghanaCard: raw.ghana_card_number || raw.ghanacard_id || "",
    gender: raw.gender || "",
    dateOfBirth: raw.date_of_birth || null,
    region: raw.region || "",
    gpsAddress: raw.gps_address || "",
    loanAmount: Number(raw.loan_amount ?? raw.amount_owed ?? owed),
    lastPaymentDate: raw.last_payment_date || null,
    amountOwed: owed,
    amountPaid: paid,
    outstandingAmount: outstanding,
    dueDate: raw.loan_due_date || raw.due_date || raw.dueDate || null,
    daysOverdue: Number(raw.days_overdue ?? raw.daysOverdue ?? 0),
    status: raw.status || "open",
    assignedAgent: assignedAgent && assignedAgent !== "Unassigned" ? assignedAgent : null,
    assignedAgentSlug: raw.call_center_agent_slug || null,
    assignedAt: raw.assigned_at || null,
    firstContactedAt: raw.first_contacted_at || null,
    paymentInitiatedAt: raw.payment_initiated_at || null,
    paymentCompletedAt: raw.payment_completed_at || null,
    slaStartedAt: raw.sla_started_at || raw.created_at || null,
    country: raw.country || null,
    exactArea: raw.exact_area || null,
    institution: extras.institution || raw.institution_name || raw.provider_name || "",
    provider_id: raw.provider_id ?? raw.provider_slug ?? null,
    groupId:
      raw.group_id != null
        ? String(raw.group_id)
        : raw.portfolio_group_slug ||
          (raw.portfolio_group_id != null ? String(raw.portfolio_group_id) : null),
    portfolioGroupName: raw.group_name || raw.portfolio_group_name || null,
    portfolioId: raw.portfolio_file_id
      ? String(raw.portfolio_file_id)
      : raw.portfolio_file_upload_id
        ? String(raw.portfolio_file_upload_id)
        : null,
    updatedAt: raw.updated_at || raw.created_at || null,
    createdAt: raw.created_at || null,
    channel: raw.interaction_method || raw.channel || null,
    canManageProvider: raw.can_manage_provider === true || raw.can_manage_provider === 1,
    escalated: raw.escalated === true || raw.escalated === 1,
  };
}

export function clearOpsWorkspaceCache() {
  for (const key of [...workspaceInFlight.keys(), ...workspaceResults.keys(), ...workspaceCachedAt.keys()]) {
    if (key.startsWith("ops-cases:") || key.startsWith("ops-escalations:") || key.startsWith("ops-cases-summary:")) {
      workspaceInFlight.delete(key);
      workspaceResults.delete(key);
      workspaceCachedAt.delete(key);
    }
  }
}

function mapAgentRow(agent) {
  return {
    id: agent.admin_slug,
    admin_slug: agent.admin_slug,
    name: agent.name || `${agent.first_name || ""} ${agent.last_name || ""}`.trim(),
    role: agent.role || "Case operator",
    status: agent.status ? "active" : "inactive",
    casesAssigned: Number(agent.cases_assigned || 0),
    contactsToday: 0,
    email: agent.email,
    permissions: agent.permissions || [],
  };
}

export function tableFiltersToApi(filters = {}) {
  const payload = {};
  if (filters.search?.trim()) payload.search = filters.search.trim();

  const name = filters.customer || filters.customer_name;
  if (Array.isArray(name) && name.length) payload.customer_name = name[0];
  else if (typeof name === "string" && name.trim()) payload.customer_name = name.trim();

  const amount = filters.amountOwed || filters.amount_owed;
  if (amount?.min !== "" && amount?.min != null) payload.amount_min = amount.min;
  if (amount?.max !== "" && amount?.max != null) payload.amount_max = amount.max;

  const outstanding = filters.outstandingAmount || filters.outstanding_amount;
  if (outstanding?.min !== "" && outstanding?.min != null) payload.outstanding_min = outstanding.min;
  if (outstanding?.max !== "" && outstanding?.max != null) payload.outstanding_max = outstanding.max;

  const due = filters.dueDate || filters.due_date;
  if (due?.from) payload.due_date_from = due.from;
  if (due?.to) payload.due_date_to = due.to;

  const created = filters.createdAt || filters.date || filters.uploadedAt;
  if (created?.from) payload.date_from = created.from;
  if (created?.to) payload.date_to = created.to;

  return payload;
}

const workspaceInFlight = new Map();
const workspaceResults = new Map();
const workspaceCachedAt = new Map();

function runDeduped(key, factory, { force = false, ttlMs = null } = {}) {
  if (!force && workspaceResults.has(key)) {
    const cachedAt = workspaceCachedAt.get(key) || 0;
    if (ttlMs == null || Date.now() - cachedAt < ttlMs) {
      return Promise.resolve(workspaceResults.get(key));
    }
    workspaceResults.delete(key);
    workspaceCachedAt.delete(key);
  }

  if (!force && workspaceInFlight.has(key)) {
    return workspaceInFlight.get(key);
  }

  const promise = Promise.resolve()
    .then(factory)
    .then((result) => {
      // Avoid sticky empty caches when an earlier request raced before data existed.
      const isEmptyArray = Array.isArray(result) && result.length === 0;
      if (!isEmptyArray) {
        workspaceResults.set(key, result);
        workspaceCachedAt.set(key, Date.now());
      } else {
        workspaceResults.delete(key);
        workspaceCachedAt.delete(key);
      }
      return result;
    })
    .catch((err) => {
      workspaceResults.delete(key);
      workspaceCachedAt.delete(key);
      throw err;
    })
    .finally(() => {
      if (workspaceInFlight.get(key) === promise) {
        workspaceInFlight.delete(key);
      }
    });

  workspaceInFlight.set(key, promise);
  return promise;
}

export function clearInstitutionPortfolioGroupsCache(slug = null) {
  if (!slug) {
    workspaceInFlight.clear();
    workspaceResults.clear();
    workspaceCachedAt.clear();
    return;
  }
  for (const key of [...workspaceInFlight.keys(), ...workspaceResults.keys(), ...workspaceCachedAt.keys()]) {
    if (key.includes(`:${slug}:`) || key.endsWith(`:${slug}`)) {
      workspaceInFlight.delete(key);
      workspaceResults.delete(key);
      workspaceCachedAt.delete(key);
    }
  }
}

export async function fetchInstitutionCustomersPage(token, slug, page = 1, limit = 50) {
  if (USE_MOCK_AUTH || !token) return { rows: [], pagination: null };
  const json = await apiRequest(
    `${institutionCustomersPath(slug)}${toQuery({ page, limit, sort_order: "desc" })}`,
    { method: "GET", token }
  );
  const data = unwrapApiEnvelope(json);
  return {
    rows: data?.debt_customers || [],
    pagination: data?.pagination || null,
  };
}

export async function fetchInstitutionCustomers(token, slug, { force = false, forceAll = true } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `customers:v2:${token}:${slug}:${forceAll ? "all" : "page1"}`;
  return runDeduped(
    cacheKey,
    () =>
      forceAll
        ? fetchAllPages(token, institutionCustomersPath(slug), (data) => data?.debt_customers || [])
        : apiRequest(institutionCustomersPath(slug), { method: "GET", token }).then(
            (json) => unwrapApiEnvelope(json)?.debt_customers || []
          ),
    { force }
  );
}

/**
 * GET /api/ops/customers — all institutions' debtors for the ops Customers page.
 */
export async function fetchOpsCustomers(token, { force = false, forceAll = true } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `ops-customers:v1:${token}:${forceAll ? "all" : "page1"}`;
  return runDeduped(
    cacheKey,
    () =>
      forceAll
        ? fetchAllPages(token, OPS_AUTH.customers, (data) => data?.debt_customers || [])
        : apiRequest(OPS_AUTH.customers, { method: "GET", token }).then(
            (json) => unwrapApiEnvelope(json)?.debt_customers || []
          ),
    { force }
  );
}

export async function fetchOpsCustomersSummary(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `ops-customers-summary:v1:${token}`;
  return runDeduped(
    cacheKey,
    async () => {
      const json = await apiRequest(OPS_AUTH.customersSummary, { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}

export async function fetchInstitutionCustomersSummary(token, slug, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `customers-summary:${token}:${slug}`;
  return runDeduped(
    cacheKey,
    async () => {
      const json = await apiRequest(institutionCustomersSummaryPath(slug), { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}

export async function fetchInstitutionPortfolioGroups(token, slug, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `groups:${token}:${slug}`;
  return runDeduped(
    cacheKey,
    () => fetchAllPages(token, institutionPortfolioGroupsPath(slug), (data) => data?.portfolio_groups || []),
    { force }
  );
}

export async function fetchInstitutionPortfolioGroup(token, slug, groupSlug, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `group:${token}:${slug}:${groupSlug}`;
  return runDeduped(
    cacheKey,
    async () => {
      const json = await apiRequest(institutionPortfolioGroupPath(slug, groupSlug), { method: "GET", token });
      return unwrapApiEnvelope(json)?.portfolio_group || null;
    },
    { force }
  );
}

export async function fetchInstitutionGroupCases(token, slug, groupSlug, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `group-cases:${token}:${slug}:${groupSlug}`;
  return runDeduped(
    cacheKey,
    () => fetchAllPages(token, institutionGroupCasesPath(slug, groupSlug), (data) => data?.cases || []),
    { force }
  );
}

export async function fetchInstitutionGroupUploads(token, slug, groupSlug, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `group-uploads:${token}:${slug}:${groupSlug}`;
  return runDeduped(
    cacheKey,
    () => fetchAllPages(token, institutionGroupUploadsPath(slug, groupSlug), (data) => data?.file_uploads || []),
    { force }
  );
}

export async function fetchInstitutionFileRecords(token, slug, groupSlug, uploadId, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `file-records:${token}:${slug}:${groupSlug}:${uploadId}`;
  return runDeduped(
    cacheKey,
    () =>
      fetchAllPages(
        token,
        institutionFileRecordsPath(slug, groupSlug, uploadId),
        (data) => data?.cases || data?.records || []
      ),
    { force }
  );
}

export async function assignOpsCase(token, caseId, agentSlug, target = {}) {
  const json = await apiRequest(caseAssignPath(caseId), {
    method: "POST",
    token,
    body: { agent_slug: agentSlug, ...target },
  });
  return unwrapApiEnvelope(json);
}

export async function fetchSmsLogs(token, filters = {}) {
  const params = new URLSearchParams();
  ["type", "status", "search", "date_from", "date_to", "limit"].forEach((key) => {
    if (filters[key]) params.set(key, String(filters[key]));
  });
  const query = params.toString();
  const json = await apiRequest(`/api/ops/sms-logs${query ? `?${query}` : ""}`, { token });
  const data = unwrapApiEnvelope(json);
  return data?.messages || [];
}

export async function fetchAssignmentTargets(token) {
  const json = await apiRequest("/api/ops/assignment-targets", { token });
  const data = unwrapApiEnvelope(json);
  return data?.batches || [];
}

export async function bulkAssignOpsCases(token, payload) {
  const json = await apiRequest(OPS_AUTH.bulkAssign, {
    method: "POST",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function previewBulkAssignOpsCases(token, payload) {
  const json = await apiRequest(OPS_AUTH.bulkAssignPreview, {
    method: "POST",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function fetchAssignableAgents(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `agents:v2:${token}`;
  return runDeduped(
    cacheKey,
    async () => {
      const json = await apiRequest(`${OPS_AUTH.agents}${toQuery({ limit: 100 })}`, { method: "GET", token });
      return (unwrapApiEnvelope(json)?.agents || []).map(mapAgentRow);
    },
    { force }
  );
}

export async function fetchOpsCases(token, query = {}, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runDeduped(
    `ops-cases:${token}:${JSON.stringify(query)}`,
    () =>
      fetchAllPages(token, OPS_AUTH.cases, (data) => data?.cases || [], {
        ...query,
        limit: CASES_PAGE_LIMIT,
      }),
    { force, ttlMs: LIST_TTL_MS }
  );
}

export async function fetchOpsCasesSummary(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runDeduped(
    `ops-cases-summary:${token}`,
    async () => {
      const json = await apiRequest(OPS_AUTH.casesSummary, { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force, ttlMs: LIST_TTL_MS }
  );
}

export async function fetchOpsCase(token, caseId) {
  if (USE_MOCK_AUTH || !token || !caseId) return null;
  const json = await apiRequest(casePath(caseId), { method: "GET", token });
  return unwrapApiEnvelope(json)?.case || null;
}

export async function fetchOpsCaseSla(token, caseId) {
  if (USE_MOCK_AUTH || !token || !caseId) return null;
  const json = await apiRequest(caseSlaPath(caseId), { method: "GET", token });
  return unwrapApiEnvelope(json);
}

export async function createOpsCallLog(token, caseId, payload) {
  const json = await apiRequest(caseCallLogsPath(caseId), {
    method: "POST",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function initiateOpsPayment(token, caseId, payload) {
  const json = await apiRequest(casePaymentInitiatePath(caseId), {
    method: "POST",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function updateOpsPayment(token, paymentId, payload) {
  const json = await apiRequest(paymentUpdatePath(paymentId), {
    method: "PATCH",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function escalateOpsCase(token, caseId, payload) {
  const json = await apiRequest(caseEscalatePath(caseId), {
    method: "POST",
    token,
    body: payload,
  });
  clearOpsWorkspaceCache();
  return unwrapApiEnvelope(json);
}

export async function pushOpsEscalation(token, escalationId, payload = {}) {
  const json = await apiRequest(`/api/ops/escalations/${encodeURIComponent(escalationId)}/push`, {
    method: "POST",
    token,
    body: payload,
  });
  clearOpsWorkspaceCache();
  return unwrapApiEnvelope(json);
}

export async function fetchOpsEscalations(token, query = {}, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runDeduped(
    `ops-escalations:${token}:${JSON.stringify(query)}`,
    () => fetchAllPages(token, OPS_AUTH.escalations, (data) => data?.escalations || [], query),
    { force, ttlMs: LIST_TTL_MS }
  );
}
